import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';
import * as dealInfoHelpers from 'shared/utils/deal-info-helpers';
import ui from 'redux-ui';

import { getNewRelic } from 'shared/analytics/new-relic';

import { WixConnect } from 'shared/utils/wix-connect';
import WixSDK from 'shared/utils/wix-sdk';
import { isMobile, isTablet } from 'shared/utils/device/screen';
import { getVideoSelectedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import {
  openFullScreenOverlay,
  openFullScreenMemberOnlyOverlay,
} from 'shared/utils/open-overlay';
import { storeForReload } from 'widget/utils/reload';
import paymentsHelper from 'widget/utils/payments';
import { stripLayoutThumbnailSize } from 'widget/utils/videos-sizes';
import { isAlwaysShow, isExpandOnClick, isNeverShow } from 'widget/utils/video-list';
import {
  isChannelShareVisible,
  isChannelInfoVisible,
  isSignInVisible,
  isSearchInputVisible,
  isCategoriesDropdownVisible,
} from 'shared/utils/layout-helpers';
import { isVisible as isSubscriptionButtonVisible } from 'widget/components/overlay-texts/channel/subscribe-button-text';

import { sides } from 'shared/constants/popout';
import editModes from 'widget/constants/edit-modes';

import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import {
  playVideo,
  pauseVideo,
} from 'widget/redux/client/actions/change-playback-status';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { isVideoPlaying } from 'widget/selectors/video-playback-status';
import { getAllSettings } from 'shared/selectors/app-settings';

import { DATA_KEY } from 'widget/data-components/paginated-channel-videos';

import BaseView from 'widget/views/_base';

import Icon from 'shared/components/icon/icon';
import { LinkButton } from 'shared/components/button/button';
import { StripWidgetDropdown } from 'shared/components/dropdown/dropdown';
import UpdateWidgetSize from 'widget/data-components/update-widget-size';
import AccountButtonStrip from 'widget/components/account/account-button-strip';
import ChannelInfoButton from 'widget/components/channel-actions/channel-info-button';
import ChannelSubscriptionButton from 'widget/components/channel-actions/channel-subscription-button';
import ShareChannel from 'widget/components/share-channel/share-channel';
import FullScreenModalPlayer from 'widget/components/players/fs-modal-player';
import MobilePlayer from 'widget/components/players/mobile-player';
import extendWithFullScreenBuy from 'widget/views/desktop/strip/player/fs-buy-extensions';
import Categories from 'widget/components/categories/categories';
import Tags from 'widget/components/tags/tags';
import { StripSearchBar } from 'widget/components/search-bar/search-bar';
import VideosListStrip from 'widget/components/videos-list/strip/strip';
import OverlayStrip from 'widget/components/player-overlay/strip/strip';
import OverlayStripMemberOnly from 'widget/components/player-overlay/strip/member-only';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import EmptySearchResults from 'widget/components/empty-search-results/empty-search-results';
import Button from 'widget/components/button/button';
import viewModes from 'shared/constants/view-modes';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';

import styles from './strip.styl';

import translations from './translations';
import widgetTranslations from 'widget/translations';

const MIN_WIDGET_HEIGHT = 480;
const SEARCH_LINE_HEIGHT = 42;
const FIXED_NO_SEARCH_RESULT_MIN_HEIGHT = 350;
const IS_MOBILE_DEVICE = isMobile() || isTablet();
const DEFAULT_STATE = {
  canShowChannelCover: true,
  isTagFocused: false,
  isSearchInputFocused: false,
  searchLayout: false,
  currentCategory: null,
  areAllVideosVisible: false,
  isInFullScreen: false,
};


const StripPlayer = extendWithFullScreenBuy(FullScreenModalPlayer);


@connect((state, ownProps) => ({
  mainVideoId: getMainVideoId(state, DATA_KEY),
  isVideoPlaying: isVideoPlaying(state),
  currentChannelId: state.currentChannelId,
  canShowChannelCover: !state.isVideosTouched,
  appSettings: getAllSettings(state),
  firstChannelVideoId: state.firstChannelVideoId,
  prevVideoId: ownProps.nextChannelVideos.prevId,
  nextVideoId: ownProps.nextChannelVideos.nextId,
  isSearchInputVisible: isSearchInputVisible(state.appSettings, ownProps.channelData),
  isCategoriesDropdownVisible: isCategoriesDropdownVisible(state.appSettings, ownProps.channelData),
}), {
  selectVideo,
  resetSelectedVideo,
  playVideo,
  pauseVideo,
})
@ui()
@WixConnect(styles)
export default class StripView extends BaseView {
  static displayName = 'StripView';

  static propTypes = {
    updateUI: React.PropTypes.func,
    isSearchInputVisible: React.PropTypes.bool,
    isCategoriesDropdownVisible: React.PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      searchBarValue: props.ui.searchQuery,
      ...DEFAULT_STATE,
      // TODO: move common logic to separate component
      ...this.getDefaultPlayerConfig(props),
    };

    getNewRelic().setCustomAttribute('layout', 'strip');
  }

  componentDidMount() {
    super.componentDidMount();
    this.resizeByContentInterval = setInterval(this.resizeByContent, 300);
  }

  componentWillReceiveProps(nextProps) {
    super.componentWillReceiveProps(nextProps);
    if (WixSDK.Utils.getViewMode() === viewModes.EDITOR && this.hasPreviewBecameActive(nextProps)) {
      this.reset(nextProps);
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    clearInterval(this.resizeByContentInterval);
  }

  get shouldRenderVideos() {
    const { appSettings } = this.props;

    return isExpandOnClick(appSettings.numbers.videoListVisibility) ?
      this.state.areAllVideosVisible :
      isAlwaysShow(appSettings.numbers.videoListVisibility);
  }

  get shouldRenderAllVideosButton() {
    const { appSettings } = this.props;
    return isExpandOnClick(appSettings.numbers.videoListVisibility) && !this.state.areAllVideosVisible;
  }

  get listThumbnailSize() {
    const widgetWidth = window.innerWidth;
    const { videosInRow } = this.props.appSettings.numbers;
    return stripLayoutThumbnailSize(widgetWidth, videosInRow);
  }

  get videosListHeight() {
    const { appSettings, channelData } = this.props;
    const { videosInRow, numberOfRows } = appSettings.numbers;
    const videosCount = Math.min(channelData.videosCount, videosInRow * numberOfRows);

    return this.listThumbnailSize.height * Math.ceil(videosCount / videosInRow);
  }

  get isInEditor() {
    return _.get(this.props.appSettings, 'editMode') === editModes.EDITOR;
  }

  isVideoPreviewMode() {
    // TODO: clean up video preview
    return false;
  }

  getDefaultPlayerConfig() {
    return {
      // TODO: move to selector or method. Probably will be used with isWidgetTouched (old canShowChannelCover)
      isVideoPreview: false,
    };
  }

  @autobind
  onThumbnailClicked(video) {
    if (video.id !== this.props.mainVideoId || this.canShowChannelCover) {
      this.sendVidSelectedBiEvent(video);
    }

    this.selectVideo(video);
  }

  @autobind
  selectNextVideo() {
    this.selectVideo({
      id: this.props.nextVideoId,
    });
  }

  @autobind
  selectPrevVideo() {
    this.selectVideo({
      id: this.props.prevVideoId,
    });
  }

  @autobind
  reset(props = this.props) {
    super.reset();

    this.setState({
      searchBarValue: '',
      ...DEFAULT_STATE,
      ...this.getDefaultPlayerConfig(props),
    });
    this.mainVideoList.toFirstPage();
  }

  @autobind
  processSubscriptionCancel() {
    const { appSettings: { siteUrl, instance }, currentChannelId } = this.props;
    paymentsHelper.cancelSubscription(currentChannelId, siteUrl, instance);
  }

  containerEl = null;

  @autobind
  setContainerEl(el) {
    this.containerEl = el;
  }

  get contentHeight() {
    if (!this.containerEl) {
      return 0;
    }
    return this.containerEl.clientHeight;
  }

  get widgetHeight() {
    return _.get(this.props, 'appSettings.requestedWidgetSize.height');
  }

  resizeByContentInterval = null;
  currentContentHeight = 0;

  @autobind
  resizeByContent() {
    const height = Math.ceil(this.contentHeight);
    if (!height || height === this.currentContentHeight) {
      return;
    }

    this.currentContentHeight = height;
    WixSDK.setHeight(height, { overflow: false });
  }

  initialWidgetHeight = 0;

  saveInitialHeight() {
    this.initialWidgetHeight = this.widgetHeight;
  }

  restoreInitialHeight() {
    const height = this.initialWidgetHeight;
    // true only is allowed at editor
    WixSDK.setHeight(height, { overflow: false });
  }

  resizeToMinHeight() {
    if (this.widgetHeight < MIN_WIDGET_HEIGHT) {
      // true only is allowed at editor
      WixSDK.setHeight(MIN_WIDGET_HEIGHT, { overflow: false });
    }
  }

  hasPreviewBecameActive(nextProps) {
    // TODO: rename to videoAsBackground
    return !this.state.isVideoPreview && this.isVideoPreviewMode(nextProps);
  }

  get tagList() {
    return _.get(this.props, 'channelData.statsInfo.tags') || [];
  }

  get categoriesList() {
    const categoriesStats = _.get(this.props, 'channelData.statsInfo.categories') || [];
    return _.map(categoriesStats, 'value');
  }

  get tags() {
    const { searchLayout, searchBarValue } = this.state;

    const tags = this.tagList;

    if (!tags.length || (!searchLayout && !searchBarValue) || this.props.ui.searchQuery) {
      return null;
    }

    return (
      <Tags styleName="tags"
            onTagFocus={this.handleTagsFocus}
            onTagBlur={this.handleTagsBlur}
            onTagClick={this.searchByTag}
            tags={tags} />
    );
  }

  get noSearchResults() {
    if (!this.hasNoSearchResults) {
      return null;
    }

    const styleNames = classnames('no-search-results', {
      stretched: this.videosListHeight < FIXED_NO_SEARCH_RESULT_MIN_HEIGHT,
    });

    return (
      <EmptySearchResults styleName={styleNames} />
    );
  }

  get videosList() {
    const { paginatedChannelVideos, channelData, mainVideoId } = this.props;
    const noVideos = !paginatedChannelVideos.ids.length;

    if (this.isFetching || noVideos || !this.mainVideo) {
      return null;
    }

    return (
      <VideosListStrip channelData={channelData}
                       paginatedChannelVideos={paginatedChannelVideos}
                       playVideo={this.playVideo}
                       onThumbnailClick={this.onThumbnailClicked}
                       thumbnailSize={this.listThumbnailSize}
                       selectedVideoId={mainVideoId}
                       styleName="videos" />
    );
  }

  get closeVideosButton() {
    const { appSettings } = this.props;
    const { showChannelShare, showChannelInfo, showSignIn } = appSettings.booleans;
    const isAllActionsDisabled = !showChannelShare && !showChannelInfo && !this.canSubscribe && !showSignIn;

    if (!this.shouldRenderVideos || isAlwaysShow(appSettings.numbers.videoListVisibility)) {
      return null;
    }

    const extendedCloseButton = (
      <span>
        <FormattedMessage {...translations.close} />
        <Icon styleName="small-close-icon"
              name="add" />
      </span>
    );

    return (
      <LinkButton styleName="action"
                  onClick={this.toggleVisibilityAllVideos}>
        {
          isAllActionsDisabled ?
            extendedCloseButton :
            <Icon styleName="normal-close-icon"
                  name="add" />
        }
      </LinkButton>
    );
  }

  get categories() {
    const { isCategoriesDropdownVisible, appSettings, ui: { searchQuery } } = this.props;

    if (!isCategoriesDropdownVisible) {
      return null;
    }

    const { searchLayout, currentCategory, searchBarValue } = this.state;
    const { videoListVisibility } = appSettings.numbers;
    const { showChannelCategories } = appSettings.booleans;

    if (
      isNeverShow(videoListVisibility) ||
      searchLayout ||
      (!this.isCategorySelected && (searchQuery || searchBarValue)) || !showChannelCategories
    ) {
      return null;
    }

    return (
      <Categories styleName="categories"
                  DropdownClass={StripWidgetDropdown}
                  height={SEARCH_LINE_HEIGHT}
                  list={this.categoriesList}
                  onCategorySelect={this.searchByCategory}
                  selectedCategory={currentCategory} />
    );
  }

  get shareChannel() {
    return (
      <ShareChannel channelData={this.props.channelData}
                    buttonsClassName={styles['share-buttons']}
                    styleName="share-channel action" />
    );
  }

  get canSubscribe() {
    const { channelData } = this.props;
    const subscriptionPriceInfo = dealInfoHelpers.getSubscriptionPriceInfo(channelData.dealInfo);

    return !_.get(channelData, 'dgsInfo.subscription') && subscriptionPriceInfo;
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      selectedVideoId: this.props.mainVideoId,
    });
  }

  get channelActions() {
    const { channelData, ui: { searchQuery }, appSettings } = this.props;
    const { searchLayout, searchBarValue } = this.state;

    const value = this.isCategorySelected ? '' : searchQuery;

    if (searchLayout || value || searchBarValue) {
      return null;
    }

    const closeVideoButton = this.closeVideosButton;

    if (
      !isChannelShareVisible(appSettings) &&
      !isChannelInfoVisible(appSettings) &&
      !isSubscriptionButtonVisible({ channel: channelData }) &&
      !isSignInVisible(appSettings) &&
      !closeVideoButton
    ) {
      return null;
    }

    return (
      <div styleName="channel-actions"
           className="qa-widget-channel-actions">
        {this.shareChannel}
        <ChannelInfoButton styleName="action"
                           className="qa-widget-channel-info"
                           channelData={channelData}
                           onClick={this.showChannelInfo} />
        <ChannelSubscriptionButton styleName="action"
                                   className="qa-widget-overlay-subscribe-button"
                                   channelData={channelData}
                                   onClick={this.openSubscription} />
        <AccountButtonStrip styleName="action"
                            className="qa-widget-channel-account"
                            channelData={channelData}
                            popoutSide={sides.TOP}
                            onUserLogin={this.storeDataForReload}
                            onUserLogout={this.storeDataForReload}
                            onSubscribeClick={this.openSubscription}
                            onSubscriptionCancelClick={this.processSubscriptionCancel} />
        {closeVideoButton}
      </div>
    );
  }

  @autobind
  showChannelInfo(event) {
    event.preventDefault();

    openFullScreenOverlay(`#/channel/${this.props.channelData.id}/info`);
  }

  @autobind
  openMembership() {
    openFullScreenMemberOnlyOverlay(this.props.channelData.id, this.mainVideo.id);
  }

  @autobind()
  processDataFromStorage(data) {
    const { selectedVideoId, openOverlay, autoplay } = data;
    const { selectVideo, resetSelectedVideo } = this.props;

    if (!selectedVideoId) {
      resetSelectedVideo();
      return;
    }

    selectVideo(selectedVideoId);
    this.toggleVisibilityAllVideos();
    if (openOverlay) {
      openFullScreenOverlay(`#/channel/${this.props.channelData.id}/video/${selectedVideoId}/${autoplay ? 'play' : ''}`);
    }
  }

  get searchBar() {
    const { isSearchInputVisible, appSettings, ui: { searchQuery } } = this.props;

    if (!isSearchInputVisible) {
      return null;
    }

    const { searchLayout, searchBarValue } = this.state;
    const value = this.isCategorySelected ? '' : searchQuery;
    const isExpanded = searchLayout || value || searchBarValue;
    const { booleans, numbers } = appSettings;
    const { videoListVisibility } = numbers;
    const { showSearch } = booleans;

    if (isNeverShow(videoListVisibility) || !showSearch) {
      return null;
    }

    return (
      <StripSearchBar styleName={classnames('search', { expanded: isExpanded })}
                      className="qa-widget-searchbar"
                      value={value}
                      onFocus={this.handleSearchInputFocus}
                      onBlur={this.handleSearchInputBlur}
                      onChange={this.setSearchBarValue}
                      onClose={this.clearSearch}
                      onSearch={this.searchByQuery} />
    );
  }

  get searchLine() {
    const searchBar = this.searchBar;
    const categories = this.categories;
    const channelActions = this.channelActions;
    const { appSettings, isVideoPlaying } = this.props;

    if (
      (!this.shouldRenderVideos && !isNeverShow(appSettings.numbers.videoListVisibility)) ||
      (IS_MOBILE_DEVICE && isVideoPlaying) ||
      !searchBar && !categories && !channelActions
    ) {
      return null;
    }

    return (
      <div styleName="search-line"
           className="qa-widget-search-line">
        {channelActions}
        {searchBar}
        {categories}
      </div>
    );
  }

  get allVideosButton() {
    const { isVideoPlaying } = this.props;
    if (!this.shouldRenderAllVideosButton || (IS_MOBILE_DEVICE && isVideoPlaying)) {
      return null;
    }

    return (
      <div styleName="all-videos">
        <Button styleName="button"
                disableTheme
                className="qa-widget-overlay-all-videos-button"
                onClick={this.toggleVisibilityAllVideos}>
          <FormattedMessage {...translations.allVideos} />
          <Icon name="add"
                styleName="icon" />
        </Button>
      </div>
    );
  }

  get videos() {
    const { channelData } = this.props;

    if (!channelData || !this.shouldRenderVideos) {
      return null;
    }

    if (!channelData.videosCount) {
      return (
        <div styleName="coming-soon"
             style={{ minHeight: this.listThumbnailSize.height }}>
          <FormattedMessage {...widgetTranslations.thisChannelIsComingSoon} />
        </div>
      );
    }

    return (
      <div styleName="videos-container"
           style={{ height: this.videosListHeight }}>
        {this.videosList}
        {this.noSearchResults}
      </div>
    );
  }

  get playerSize() {
    return {
      width: window.innerWidth,
      height: Math.round(window.innerWidth / 2.39),
    };
  }

  @autobind
  playVideo(videoData) {
    const { currentSiteUser, channelData, mainVideoId } = this.props;
    const { id, memberOnly } = videoData;

    if (IS_MOBILE_DEVICE) {
      // we can`t start playing for not rendered player because of browser restriction
      if (id === mainVideoId) {
        return super.playVideo(videoData);
      }
      return this.onThumbnailClicked(videoData);
    }

    if (!memberOnly || memberOnly && currentSiteUser.data) {
      openFullScreenOverlay(`#/channel/${channelData.id}/video/${id}/play`);
    } else {
      openFullScreenMemberOnlyOverlay(channelData.id, id);
    }
  }

  get hasBottomContent() {
    // TODO move hasBottomContent to selector|method
    const allVideosButton = this.allVideosButton;
    const searchLine = this.searchLine;
    return Boolean(allVideosButton || searchLine);
  }

  get noVideoPlayerOverlay() {
    const { appSettings, channelData } = this.props;
    const hasBottomContent = this.hasBottomContent;

    const buttonsWrapperClassName = classnames({
      [styles['buttons-wrapper-no-margin']]: !hasBottomContent,
    });

    const props = {
      appSettings,
      channelData,
      buttonsWrapperClassName,
      styleName: 'player-overlay',
      showChannelCover: true,
      areAllVideosVisible: false,
      videoItem: {},
    };

    return (
      <OverlayStrip {...props} />
    );
  }

  getPlayerOverlay(videoItem) {
    const { appSettings, channelData, prevVideoId, nextVideoId } = this.props;
    const hasBottomContent = this.hasBottomContent;

    const buttonsWrapperClassName = classnames({
      [styles['buttons-wrapper-no-margin']]: !hasBottomContent,
    });

    return (
      <OverlayStrip styleName="player-overlay"
                    buttonsWrapperClassName={buttonsWrapperClassName}
                    channelData={channelData}
                    appSettings={appSettings}
                    videoItem={videoItem}
                    showChannelCover={this.canShowChannelCover}
                    playSelectedVideo={this.playVideo}
                    nextVideoId={nextVideoId}
                    prevVideoId={prevVideoId}
                    playNextVideo={this.playNextVideo}
                    selectNextVideo={this.selectNextVideo}
                    selectPrevVideo={this.selectPrevVideo}
                    areAllVideosVisible={this.shouldRenderVideos}
                    toggleVisibilityAllVideos={this.toggleVisibilityAllVideos}
                    showChannelInfo={this.showChannelInfo} />
    );
  }

  get memberOnlyOverlay() {
    const { prevVideoId, nextVideoId } = this.props;

    const hasBottomContent = this.hasBottomContent;

    const buttonsWrapperClassName = classnames({
      [styles['buttons-wrapper-no-margin']]: !hasBottomContent,
    });

    return (
      <OverlayStripMemberOnly className={styles['player-overlay']}
                              buttonsWrapperClassName={buttonsWrapperClassName}
                              nextVideoId={nextVideoId}
                              prevVideoId={prevVideoId}
                              selectNextVideo={this.selectNextVideo}
                              selectPrevVideo={this.selectPrevVideo} />
    );
  }

  get player() {
    const mainVideo = this.mainVideo;
    const { width, height } = this.playerSize;
    const { channelData, isVideoPlaying } = this.props;

    const playerProps = {
      videoItem: mainVideo,
      key: mainVideo.id,
      channelData,
      width, height,
      canShowChannelCover: this.canShowChannelCover,
      styleName: 'player',
      className: 'qa-widget-player',
      memberOnlyOverlay: this.memberOnlyOverlay,
      onMemberSignUp: this.openMembership,
      onFullScreenChanged: this.setFullScreenState,
    };

    if (IS_MOBILE_DEVICE) {
      return (
        <MobilePlayer {...playerProps}
                      paused={!isVideoPlaying}>
          {this.getPlayerOverlay(mainVideo)}
        </MobilePlayer>
      );
    }

    return (
      <StripPlayer {...playerProps}
                   onMemberSignUp={this.openMembership}>
        {this.getPlayerOverlay(mainVideo)}
      </StripPlayer>
    );
  }

  get playerContainer() {
    const mainVideo = this.mainVideo;

    if (!mainVideo) {
      return (
        <div styleName="player-container">
          {this.noVideosPlayerContent}
          {this.allVideosButton}
          {this.searchLine}
        </div>
      );
    }

    const hasBottomContent = this.hasBottomContent;

    return (
      <div styleName={classnames('player-container', { 'with-bottom-padding': hasBottomContent })}
           data-channel-layout="strip">
        {this.player}
        {this.allVideosButton}
        {this.searchLine}
      </div>
    );
  }

  @autobind
  toggleVisibilityAllVideos() {
    this.setState({
      areAllVideosVisible: !this.state.areAllVideosVisible,
    });
  }

  sendVidSelectedBiEvent({ id }) {
    const { appSettings, channelData, videoByIds } = this.props;
    this.props.biEvent(
      'widget.VidSelected.displayed',
      getVideoSelectedEventParams(appSettings, channelData, videoByIds[id], this.canShowChannelCover),
    );
  }

  get isVideosLoaded() {
    const { videoByIds, paginatedChannelVideos } = this.props;
    return !paginatedChannelVideos.isFetching || !_.isEmpty(videoByIds);
  }

  render() {
    if (!this.isUIBlockingDataLoaded || !this.isVideosLoaded) {
      return this.spinner;
    }

    const { appSettings } = this.props;
    const styleNames = classnames('container', `view-mode-${appSettings.editMode}`);

    return (
      <section styleName={styleNames}
               ref={this.setContainerEl}
               className="qa-widget-container">
        <UpdateWidgetSize disabled={this.state.isInFullScreen} />
        <ReloadStorage onReload={this.processDataFromStorage} />

        {this.playerContainer}
        {this.videos}

        <PaymentEvents onRent={this.setCurrentVideoFromPayment}
                       onSale={this.setCurrentVideoFromPayment}
                       onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/desktop/strip/strip.js